/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import { Panel, PanelType, Stack, Text } from "@fluentui/react";
import React from "react";
import { IFieldDefinitionPanelHeadingProps, IFieldDefinitionPanelHeadingState } from ".";
import { HuxTextBox } from "@nuance/hux-components";
import AccessLevelSelection from "./AccessLevelSelection";
import { FieldDefinitionPanelHeadingCommands } from "./FieldDefinitionPanelHeadingViewModel";
import { panelFooterProps, panelProps } from "./FieldDefinitionPanelStackTokens";
import PanelFooter from "./PanelFooter";
import { IPanelFooterProps } from "./IPanelFooterProps";

const fieldDefinitionPanelHeadingCommands = new FieldDefinitionPanelHeadingCommands();
const cmds = fieldDefinitionPanelHeadingCommands.Cmds;

const FieldDefinitionPanelHeading = (props: IFieldDefinitionPanelHeadingProps): JSX.Element => {
    const initialState: IFieldDefinitionPanelHeadingState = {
        common: { ...props.common },
        data: { ...props.data },
        errors: {},
        touched: {},
        isValid: !props.common.isNew,
        isDirty: false
    };

    const [state, dispatch] = React.useReducer(
        fieldDefinitionPanelHeadingCommands.Reducer,
        initialState
    );

    const onSaveFieldDefinition = () => {
        // save the thing...
        if (props.common.onSaveContent !== undefined) {
            props.common.onSaveContent(fieldDefinitionPanelHeadingCommands.Save(state));
        }
    };

    const footerProps: IPanelFooterProps = {
        onSave: onSaveFieldDefinition,
        onCancel: props.common.onClosePanel,
        isNew: state.common.isNew,
        isDirty: state.isDirty,
        isValid: state.isValid,
        panelFooterProps
    };

    return (
        <Panel
            type={PanelType.medium}
            isOpen={props.common.isOpen}
            onDismiss={props.common.onClosePanel}
            headerText={props.common.panelTitle}
            closeButtonAriaLabel="Close"
            onRenderFooterContent={() => {
                return PanelFooter(footerProps);
            }}
            // Stretch panel content to fill the available height so the footer is positioned
            // at the bottom of the page
            isFooterAtBottom={true}
        >
            <Stack {...panelProps}>
                <Text block>
                    Headings are used to help collect settings together. If a heading is added, all
                    settings within the same section should also be grouped under headings.
                </Text>
                <HuxTextBox
                    required
                    label="Heading Id"
                    name="headingId"
                    value={state.data.headingId}
                    errorMessage={state.touched.headingId ? state.errors.headingId : ""}
                    onChange={(e, value) =>
                        dispatch(
                            cmds.onTextChanged({
                                name: e.currentTarget.name,
                                value
                            })
                        )
                    }
                    onBlur={e => dispatch(cmds.onBlur(e.currentTarget.name))}
                    disabled={!props.common.isNew}
                />

                <HuxTextBox
                    required
                    label="Heading label"
                    name="heading"
                    value={state.data.heading}
                    errorMessage={state.touched.heading ? state.errors.heading : ""}
                    onChange={(e, value) =>
                        dispatch(
                            cmds.onTextChanged({
                                name: e.currentTarget.name,
                                value
                            })
                        )
                    }
                    onBlur={e => dispatch(cmds.onBlur(e.currentTarget.name))}
                />

                <AccessLevelSelection
                    accessLevel={state.data.accessLevels}
                    onAccessLevelChange={accessLevel =>
                        dispatch(cmds.onAccessLevelChanged(accessLevel))
                    }
                />
            </Stack>
        </Panel>
    );
};

const HeadingPanel = FieldDefinitionPanelHeading;
export { HeadingPanel };
